// Project page phase schedule: Gantt (one row per JLL phase) and, for
// admin/editor only, a Schedule editor with ordinary component-local
// draft / Save / Cancel state. The server enforces the same role gate.
//
// NOTE: the external Market PM (JLL) role folds into "editor" here until
// those users exist; no separate role is recognised by this UI.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { DateInput } from "../../../components/DateInput.jsx";
import {
  GANTT_LEGEND, todayUtcMs, parseIsoDay,
  buildScheduleRow, buildScheduleScale, buildScheduleSaveBody, draftFromSchedule,
  isDraftDirty, normalizePhases, phaseInterval, scheduleFromDraft, validateScheduleDraft,
} from "../hub/ganttModel.js";
import { ScheduleTimeline } from "../hub/ScheduleTimeline.jsx";
import { getProjectSchedule, saveProjectSchedule, SCHEDULE_SAVED_EVENT } from "../../../data/projectScheduleClient.js";

const SCHEDULE_EDIT_ROLES = new Set(["admin", "editor"]);

function phaseRows(project, schedule) {
  const row = buildScheduleRow(project, schedule);
  return normalizePhases(schedule).map((p) => {
    const iv = phaseInterval(p);
    const bar = row.bars.find((b) => b.key === p.phase);
    return {
      id: p.phase, name: p.label, swatch: p.color,
      kind: bar ? "bar" : iv.kind,
      bars: bar ? [bar] : [],
      endpoints: [parseIsoDay(p.startDate), parseIsoDay(p.endDate)].filter(v => v != null),
      markers: row.markers,
      emptyText: iv.kind === "partial" ? "One date recorded" : iv.kind === "invalid" ? iv.reason : "Not scheduled",
    };
  });
}

function ScheduleGantt({ project, schedule, testPrefix, keyTestId }) {
  const today = useMemo(() => todayUtcMs(new Date()), []);
  const rows = useMemo(() => phaseRows(project, schedule), [project, schedule]);
  const scale = useMemo(() => buildScheduleScale(rows, today), [rows, today]);
  return <ScheduleTimeline rows={rows} scale={scale} today={today} nameWidth={190} nameHeader="PHASE" testPrefix={testPrefix} keyTestId={keyTestId} showCommencement />;
}

const btn = (primary) => ({
  border: `1px solid ${primary ? T.projectsHubNavy : T.border}`, background: primary ? T.projectsHubNavy : T.card,
  color: primary ? T.white : T.projectsHubNavy, borderRadius: 8, padding: "7px 14px", fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer",
});

function DateField({ id, label, value, onChange, error, disabled }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label htmlFor={id} style={{ display: "block", fontSize: T.fsXxs, color: T.mid, marginBottom: 3 }}>{label}</label>
      <DateInput id={id} data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        aria-invalid={error ? "true" : undefined} aria-describedby={error ? `${id}-err` : undefined}
        style={error ? { borderColor: T.red } : undefined} />
      {error && <div id={`${id}-err`} role="alert" data-testid={`${id}-error`} style={{ fontSize: T.fsXxs, color: T.red, marginTop: 3 }}>{error}</div>}
    </div>
  );
}

export function ProjectSchedule({ projectId, project, currentRole, onSaved, onEditorState }) {
  const canEdit = SCHEDULE_EDIT_ROLES.has(currentRole);
  const [load, setLoad] = useState({ status: "loading", schedule: null, error: null });
  const [retry, setRetry] = useState(0);
  const [mode, setMode] = useState("gantt");
  const [draft, setDraft] = useState(null);
  const [original, setOriginal] = useState(null);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Report editor state ({ editing, busy }) to the record owner via a ref.
  const stateCb = useRef(onEditorState);
  stateCb.current = onEditorState;
  useEffect(() => () => stateCb.current?.({ editing: false, busy: false }), []);

  useEffect(() => {
    let active = true;
    setLoad((l) => ({ status: "loading", schedule: l.schedule?.projectId === projectId ? l.schedule : null, error: null }));
    Promise.resolve().then(() => getProjectSchedule(projectId))
      .then((s) => { if (active) setLoad({ status: "ok", schedule: s, error: null }); })
      .catch((error) => { if (active) setLoad({ status: "error", schedule: null, error }); });
    return () => { active = false; };
  }, [projectId, retry, project?.fdobDate, project?.commencementDate]);

  useEffect(() => { if (!canEdit) setMode("gantt"); }, [canEdit]);

  const schedule = load.schedule;
  const openEditor = () => {
    if (draft) { setMode("editor"); return; }
    const d = draftFromSchedule(schedule);
    setDraft(d); setOriginal(d); setTouched(false); setSaveError(null); setMode("editor");
  };
  const cancel = () => { if (saving) return; setDraft(null); setOriginal(null); setSaveError(null); setTouched(false); setMode("gantt"); };

  const errors = useMemo(() => (draft ? validateScheduleDraft(draft) : {}), [draft]);
  const hasErrors = Object.keys(errors).length > 0;
  const dirty = draft && original ? isDraftDirty(draft, original) : false;
  useEffect(() => { stateCb.current?.({ editing: mode === "editor" || dirty, busy: saving }); }, [mode, dirty, saving]);
  const preview = useMemo(() => (draft ? scheduleFromDraft(draft, schedule) : schedule), [draft, schedule]);

  const setPhase = (key, field, value) => { setTouched(true); setDraft((d) => ({ ...d, phases: { ...d.phases, [key]: { ...d.phases[key], [field]: value } } })); };
  const setMarker = (field, value) => { setTouched(true); setDraft((d) => ({ ...d, [field]: value })); };

  const save = useCallback(() => {
    if (!draft || hasErrors || saving || !canEdit) return;
    setSaving(true); setSaveError(null);
    Promise.resolve().then(() => saveProjectSchedule(projectId, buildScheduleSaveBody(draft, original)))
      .then((saved) => {
        setLoad({ status: "ok", schedule: saved, error: null });
        setSaving(false); setDraft(null); setOriginal(null); setMode("gantt");
        onSaved?.(saved);
        try { window.dispatchEvent(new CustomEvent(SCHEDULE_SAVED_EVENT, { detail: { projectId } })); } catch { /* non-DOM env */ }
      })
      .catch((err) => { setSaving(false); setSaveError(err?.message || "Save failed."); });
  }, [draft, original, hasErrors, saving, canEdit, projectId, onSaved]);

  const approx = normalizePhases(schedule).filter((p) => !p.verified && (p.startDate || p.endDate));
  const sources = [...new Set(approx.map((p) => p.source).filter(Boolean))];

  return (
    <section data-testid="project-schedule" aria-labelledby="project-schedule-title" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, marginBottom: 14, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
        <h2 id="project-schedule-title" style={{ margin: 0, fontSize: T.fsXs, fontWeight: T.fwHubTitle, letterSpacing: ".05em", textTransform: "uppercase", color: T.projectsHubNavy }}>Phase schedule</h2>
        {approx.length > 0 && (
          <span data-testid="project-schedule-approx" title={sources.join(", ")} style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.amberDark, background: T.lightGray, borderRadius: 999, padding: "2px 9px" }}>
            Approximate · unverified{sources.length ? ` · ${sources.join(", ")}` : ""}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {canEdit && load.status === "ok" && (
          <div role="group" aria-label="Schedule view" style={{ display: "inline-flex", border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
            {[["gantt", "Gantt"], ["editor", "Schedule editor"]].map(([k, label]) => (
              <button key={k} type="button" data-testid={`project-schedule-mode-${k}`} aria-pressed={mode === k}
                onClick={() => { if (k === mode) return; if (k === "editor") openEditor(); else if (!dirty) cancel(); else setMode("gantt"); }}
                style={{ border: "none", padding: "6px 12px", fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", background: mode === k ? T.projectsHubNavy : T.card, color: mode === k ? T.white : T.projectsHubNavy }}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {load.status === "loading" && !schedule && (
        <div data-testid="project-schedule-loading" aria-busy="true" style={{ padding: 16 }}>
          {GANTT_LEGEND.map((l, i) => <div key={l.key} style={{ height: 12, width: `${30 + ((i * 11) % 50)}%`, marginLeft: `${i * 6}%`, borderRadius: 6, background: T.lightGray, marginBottom: 10 }} />)}
        </div>
      )}
      {load.status === "error" && (
        <div role="alert" data-testid="project-schedule-error" style={{ padding: "14px 18px", fontSize: T.fsSm, color: T.red }}>
          Couldn't load the phase schedule: {load.error?.message || "request failed"}
          <button type="button" data-testid="project-schedule-retry" onClick={() => setRetry((n) => n + 1)} style={{ ...btn(false), marginLeft: 10, padding: "3px 10px" }}>Retry</button>
        </div>
      )}

      {schedule && (mode === "gantt" || !draft) && (
        <div data-testid="project-schedule-gantt">
          <ScheduleGantt project={project} schedule={schedule} testPrefix="schedule" keyTestId="project-schedule-key" />
          {dirty && canEdit && (
            <div style={{ padding: "8px 18px", fontSize: T.fsXs, color: T.mid }}>
              Unsaved schedule changes are kept. <button type="button" onClick={() => setMode("editor")} style={{ ...btn(false), padding: "2px 8px" }}>Back to editor</button>
            </div>
          )}
        </div>
      )}

      {canEdit && schedule && mode === "editor" && draft && (
        <div data-testid="project-schedule-editor" style={{ padding: 14 }}>
          <div style={{ background: T.blueSoft, border: `1px solid ${T.blueSoftBorder}`, borderRadius: 10, padding: "14px 16px", boxShadow: `inset 3px 0 0 ${T.blue}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "12px 18px" }}>
              {GANTT_LEGEND.map((l) => (
                <fieldset key={l.key} data-testid={`schedule-phase-${l.key}`} style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}>
                  <legend style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 2, background: l.color }} />{l.label}
                  </legend>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <DateField id={`schedule-${l.key}-start`} label="Start" value={draft.phases[l.key].startDate} onChange={(v) => setPhase(l.key, "startDate", v)} error={errors[`${l.key}.startDate`]} disabled={saving} />
                    <DateField id={`schedule-${l.key}-end`} label="End" value={draft.phases[l.key].endDate} onChange={(v) => setPhase(l.key, "endDate", v)} error={errors[`${l.key}.endDate`]} disabled={saving} />
                  </div>
                </fieldset>
              ))}
              <fieldset style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}>
                <legend style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>Markers</legend>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <DateField id="schedule-fdob" label="FDOB" value={draft.fdobDate} onChange={(v) => setMarker("fdobDate", v)} error={errors.fdobDate} disabled={saving} />
                  <DateField id="schedule-commencement" label="Commencement" value={draft.commencementDate} onChange={(v) => setMarker("commencementDate", v)} error={errors.commencementDate} disabled={saving} />
                </div>
              </fieldset>
            </div>
            <p style={{ margin: "10px 0 0", fontSize: T.fsXxs, color: T.mid }}>A phase draws a bar only when both dates are set. Source and verification are recorded by the server.</p>
            {saveError && (
              <div role="alert" data-testid="project-schedule-save-error" style={{ marginTop: 10, fontSize: T.fsSm, color: T.red }}>Not saved: {saveError}. Your changes are still here.</div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button type="button" data-testid="project-schedule-cancel" onClick={cancel} disabled={saving} style={btn(false)}>Cancel</button>
              <button type="button" data-testid="project-schedule-save" onClick={save} disabled={saving || hasErrors || !dirty}
                style={{ ...btn(true), opacity: saving || hasErrors || !dirty ? 0.55 : 1, cursor: saving || hasErrors || !dirty ? "not-allowed" : "pointer" }}>
                {saving ? "Saving…" : "Save schedule"}
              </button>
            </div>
          </div>
          <div data-testid="project-schedule-preview" aria-label="Live preview" style={{ marginTop: 12, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "6px 12px", fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid, letterSpacing: ".07em", background: "var(--v2-tint)" }}>LIVE PREVIEW{touched ? "" : " · no changes yet"}</div>
            <ScheduleGantt project={project} schedule={preview} testPrefix="schedule-preview" />
          </div>
        </div>
      )}
    </section>
  );
}
